import { Dropdown, ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  GET_VEHICLES_MAKE_RESPONSE,
  GET_VEHICLES_MODEL_RESPONSE,
  getVehiclesMake,
  getVehiclesModel,
  getVehiclesYear,
} from '@/redux/modules/search';
import {
  getMakeVehicles,
  getModelVehicles,
  getYearVehicles,
} from '@/redux/selectors/search/vehicle';

interface IVehicleInfo {
  year?: number;
  make?: string;
  modelId?: number;
  towingCapacity?: number;
}

interface ITowableVehicleSelectorProps {
  onVehicleChange: (vehicleInfo: IVehicleInfo) => void;
  initialVehicleInfo?: IVehicleInfo;
}

const TowableVehicleSelector: React.FC<ITowableVehicleSelectorProps> = ({
  onVehicleChange,
  initialVehicleInfo,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [vehicle, setVehicle] = useState<IVehicleInfo>(
    initialVehicleInfo || {
      year: undefined,
      make: undefined,
      modelId: undefined,
      towingCapacity: undefined,
    },
  );

  const yearVehicles = useSelector(getYearVehicles);
  const makeVehicles = useSelector(getMakeVehicles);
  const modelVehicles = useSelector(getModelVehicles);

  const yearOptions = yearVehicles.map(v => ({
    label: v.year.toString(),
    value: v.year.toString(),
  }));

  const makeOptions = makeVehicles.map(v => ({
    label: v.make,
    value: v.make,
  }));

  const modelOptions = modelVehicles.map(v => ({
    label: v.model,
    value: v.id.toString(),
  }));

  // Load years on mount
  useEffect(() => {
    dispatch(getVehiclesYear());
  }, [dispatch]);

  // Load make and model options if initialVehicleInfo is provided
  useEffect(() => {
    if (initialVehicleInfo?.year) {
      dispatch(getVehiclesMake(initialVehicleInfo.year));
    }
    if (initialVehicleInfo?.year && initialVehicleInfo?.make) {
      dispatch(getVehiclesModel(initialVehicleInfo.year, initialVehicleInfo.make));
    }
  }, [dispatch, initialVehicleInfo]);

  const handleYearChange = (value: string) => {
    if (value === '') {
      // Clear all selections
      setVehicle({
        year: undefined,
        make: undefined,
        modelId: undefined,
        towingCapacity: undefined,
      });
      dispatch({ type: GET_VEHICLES_MODEL_RESPONSE, payload: [] });
      dispatch({ type: GET_VEHICLES_MAKE_RESPONSE, payload: [] });
      onVehicleChange({});
      return;
    }

    const year = Number(value);
    setVehicle({
      year,
      make: undefined,
      modelId: undefined,
      towingCapacity: undefined,
    });
    dispatch(getVehiclesMake(year));
    dispatch({ type: GET_VEHICLES_MODEL_RESPONSE, payload: [] });
    onVehicleChange({ year });
  };

  const handleMakeChange = (value: string) => {
    if (value === '') {
      setVehicle(prev => ({
        ...prev,
        make: undefined,
        modelId: undefined,
        towingCapacity: undefined,
      }));
      dispatch({ type: GET_VEHICLES_MODEL_RESPONSE, payload: [] });
      onVehicleChange({ year: vehicle.year });
      return;
    }

    setVehicle(prev => ({
      ...prev,
      make: value,
      modelId: undefined,
      towingCapacity: undefined,
    }));
    dispatch(getVehiclesModel(vehicle.year!, value));
    onVehicleChange({ year: vehicle.year, make: value });
  };

  const handleModelChange = (value: string) => {
    if (value === '') {
      setVehicle(prev => ({
        ...prev,
        modelId: undefined,
        towingCapacity: undefined,
      }));
      onVehicleChange({ year: vehicle.year, make: vehicle.make });
      return;
    }

    const modelId = Number(value);
    const selectedModel = modelVehicles.find(v => v.id === modelId);

    if (selectedModel) {
      const updatedVehicle = {
        year: selectedModel.year,
        make: selectedModel.make,
        modelId: selectedModel.id,
        towingCapacity: selectedModel.max_towing_capacity,
      };
      setVehicle(updatedVehicle);
      onVehicleChange(updatedVehicle);
    }
  };

  const yearLabel = intl.formatMessage({ defaultMessage: 'Year', id: 'IFo1oo' });
  const makeLabel = intl.formatMessage({ defaultMessage: 'Make', id: '6AAM0P' });
  const modelLabel = intl.formatMessage({ defaultMessage: 'Model', id: 'rhSI1/' });

  return (
    <div className="p-4 mt-6 border rounded-lg bg-neutral-05 border-neutral-20">
      <Text
        variant={ETextStyleVariant.LargeBold}
        className="mb-5 text-base font-medium semiHighlight text-primary-base">
        <FormattedMessage
          defaultMessage="Enter your vehicle info to see what you can tow."
          id="sYl5XM"
        />
      </Text>

      <div className="flex flex-col gap-4">
        <Dropdown
          label={yearLabel}
          ariaLabel={yearLabel}
          inputValue={vehicle.year ? String(vehicle.year) : ''}
          onChange={handleYearChange}
          sheetClassName="z-[1410]">
          {yearOptions.map(year => (
            <Dropdown.Option key={year.value} value={year.value} content={{ label: year.label }} />
          ))}
        </Dropdown>

        <Dropdown
          label={makeLabel}
          ariaLabel={makeLabel}
          inputValue={vehicle.make || ''}
          onChange={handleMakeChange}
          disabled={makeOptions.length === 0}
          searchPlaceholder={makeLabel}
          sheetClassName="z-[1410] [&>div]:h-full"
          searchEnabled>
          {makeOptions.map(make => (
            <Dropdown.Option key={make.value} value={make.value} content={{ label: make.label }} />
          ))}
        </Dropdown>

        <Dropdown
          label={modelLabel}
          ariaLabel={modelLabel}
          inputValue={vehicle.modelId ? String(vehicle.modelId) : ''}
          onChange={handleModelChange}
          disabled={modelOptions.length === 0}
          searchPlaceholder={modelLabel}
          searchEnabled
          sheetClassName="z-[1410] [&>div]:h-full">
          {modelOptions.map(model => (
            <Dropdown.Option
              key={model.value}
              value={model.value}
              content={{ label: model.label }}
            />
          ))}
        </Dropdown>
      </div>
    </div>
  );
};

export default TowableVehicleSelector;
